import React from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { NavLink } from "react-router-dom";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const contactInfo = {
    phone: "+64 22 106 2796",
    email: "Franco.stander@trademin.co.nz",
    address: "Ashburton, Canterbury",
  };

  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/our-services" },
    { name: "About", path: "/about-us" },
    { name: "Contact", path: "/contact-us" },
    { name: "Get Quote", path: "/request-quote" },
    { name: "Privacy Policy", path: "/privacy-policy" },
  ];

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

    .footer-root {
      font-family: 'DM Sans', sans-serif;
      background: #0e0e0c;
      border-top: 1px solid rgba(255,255,255,0.06);
    }

    /* Main grid */
    .footer-main {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }

    .footer-col {
      padding: 4rem 3.5rem;
      border-right: 1px solid rgba(255,255,255,0.06);
    }
    .footer-col:last-child { border-right: none; }

    /* Brand col */
    .footer-brand-name {
      font-family: 'DM Serif Display', serif;
      font-size: 1.5rem;
      color: #fff;
      margin-bottom: 1rem;
      line-height: 1;
    }
    .footer-brand-name em { color: #E78730; font-style: italic; }

    .footer-brand-desc {
      font-size: 0.82rem;
      color: rgba(255,255,255,0.3);
      line-height: 1.8;
      font-weight: 300;
      max-width: 28ch;
    }

    /* Col headings */
    .footer-col-label {
      font-size: 0.62rem;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: #E78730;
      font-weight: 500;
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.6rem;
    }
    .footer-col-label::before {
      content: '';
      display: block;
      width: 14px; height: 1px;
      background: #E78730;
      flex-shrink: 0;
    }

    /* Links */
    .footer-links { list-style: none; padding: 0; margin: 0; }
    .footer-links li { margin-bottom: 0.75rem; }
    .footer-links a {
      font-size: 0.82rem;
      color: rgba(255,255,255,0.35);
      text-decoration: none;
      font-weight: 300;
      letter-spacing: 0.04em;
      transition: color 0.2s;
    }
    .footer-links a:hover { color: #fff; }

    /* Contact items */
    .footer-contact-item {
      display: flex;
      align-items: flex-start;
      gap: 0.85rem;
      margin-bottom: 1rem;
    }
    .footer-contact-item:last-child { margin-bottom: 0; }

    .footer-contact-icon { color: #E78730; flex-shrink: 0; margin-top: 1px; }

    .footer-contact-item a,
    .footer-contact-item p {
      font-size: 0.82rem;
      color: rgba(255,255,255,0.35);
      text-decoration: none;
      font-weight: 300;
      line-height: 1.5;
      transition: color 0.2s;
      margin: 0;
    }
    .footer-contact-item a:hover { color: #fff; }

    /* Hours */
    .footer-hours-item {
      font-size: 0.82rem;
      color: rgba(255,255,255,0.35);
      font-weight: 300;
      margin-bottom: 0.65rem;
      line-height: 1.5;
    }
    .footer-hours-item:last-child { margin-bottom: 0; }
    .footer-hours-day { color: rgba(255,255,255,0.55); font-weight: 400; }

    /* Bottom bar — mirrors navbar height/style */
    .footer-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 3.5rem;
      height: 72px;
      gap: 1rem;
      flex-wrap: wrap;
    }

    .footer-copyright {
      font-size: 0.68rem;
      letter-spacing: 0.1em;
      color: rgba(255,255,255,0.2);
      font-weight: 300;
    }

    /* Lightning bolt mark — same as navbar */
    .footer-mark {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      opacity: 0.4;
    }

    .footer-mark-text {
      font-size: 0.62rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.5);
      font-weight: 400;
    }

    @media (max-width: 1024px) {
      .footer-main { grid-template-columns: 1fr 1fr; }
      .footer-col { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.06); }
      .footer-col:nth-child(odd) { border-right: 1px solid rgba(255,255,255,0.06); }
      .footer-col:last-child { border-bottom: none; }
      .footer-col:nth-last-child(2) { border-bottom: none; }
    }

    @media (max-width: 600px) {
      .footer-main { grid-template-columns: 1fr; }
      .footer-col { border-right: none; }
      .footer-col:nth-child(odd) { border-right: none; }
      .footer-col:last-child { border-bottom: none; }
      .footer-bottom { padding: 1.5rem 2rem; height: auto; }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <footer className="footer-root">
        <div className="footer-main">
          {/* Brand */}
          <div className="footer-col">
            <div className="footer-brand-name">
              Trade<em>min</em>
            </div>
            <p className="footer-brand-desc">
              Powering homes, businesses, and industries with trusted electrical
              and mechanical expertise.
            </p>
          </div>

          {/* Quick Links */}
          <div className="footer-col">
            <div className="footer-col-label">Quick Links</div>
            <ul className="footer-links">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <NavLink to={link.path}>{link.name}</NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="footer-col">
            <div className="footer-col-label">Contact</div>
            <div className="footer-contact-item">
              <Phone
                size={13}
                strokeWidth={1.5}
                className="footer-contact-icon"
              />
              <a href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</a>
            </div>
            <div className="footer-contact-item">
              <Mail
                size={13}
                strokeWidth={1.5}
                className="footer-contact-icon"
              />
              <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
            </div>
            <div className="footer-contact-item">
              <MapPin
                size={13}
                strokeWidth={1.5}
                className="footer-contact-icon"
              />
              <p>{contactInfo.address}</p>
            </div>
          </div>

          {/* Hours */}
          <div className="footer-col">
            <div className="footer-col-label">Hours</div>
            <div className="footer-hours-item">
              <span className="footer-hours-day">Mon – Fri</span> &nbsp;7:00 AM
              – 6:00 PM
            </div>
            <div className="footer-hours-item">
              <span className="footer-hours-day">Saturday</span> &nbsp;8:00 AM –
              2:00 PM
            </div>
            <div className="footer-hours-item">
              <span className="footer-hours-day">Sunday</span> &nbsp;Emergency
              Only
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="footer-bottom">
          <span className="footer-copyright">
            © {currentYear} Trademin Ltd. All rights reserved.
          </span>
          <div className="footer-mark">
            <svg width="14" height="14" viewBox="0 0 32 32" fill="none">
              <polygon
                points="19,3 10,17 16,17 13,29 23,14 17,14"
                fill="#E78730"
              />
            </svg>
            <span className="footer-mark-text">Canterbury, New Zealand</span>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
